import { ShieldCheck, Info } from "lucide-react";

type PlatformName =
  | "TikTok"
  | "Instagram"
  | "YouTube"
  | "Spotify"
  | "Twitch"
  | "X (Twitter)"
  | "Facebook"
  | "all major social platforms";

interface ComplianceDisclaimerProps {
  platform?: PlatformName;
  variant?: "compact" | "full";
  className?: string;
}

/**
 * Legal / Google Ads compliance disclaimer.
 *
 * Goals:
 *  - Make it explicit to users, regulators, AI crawlers and ad-review systems
 *    that we operate as a legitimate third-party promotion / marketing service.
 *  - Avoid prohibited claims (no "buy followers", no "guaranteed results",
 *    no impersonation of the platforms themselves).
 *  - Comply with Google Ads "Misrepresentation" and "Dishonest behavior" policies,
 *    and each social network's third-party developer / marketing policies.
 */
export const ComplianceDisclaimer = ({
  platform = "all major social platforms",
  variant = "compact",
  className = "",
}: ComplianceDisclaimerProps) => {
  if (variant === "compact") {
    return (
      <div
        className={`flex items-start gap-2 rounded-lg border border-border/60 bg-muted/30 px-3 py-2 text-[11px] leading-relaxed text-muted-foreground ${className}`}
        role="note"
        aria-label="Service disclaimer"
      >
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
        <p>
          <strong className="text-foreground">Disclaimer:</strong> TokDadddy is an
          independent third-party marketing & promotion service. We are{" "}
          <strong>not affiliated with, endorsed by, or sponsored by</strong>{" "}
          {platform}. All trademarks belong to their respective owners. We comply
          with each platform's terms of service and Google Ads policies.
        </p>
      </div>
    );
  }

  return (
    <div
      className={`rounded-xl border border-border bg-card/50 p-4 text-xs leading-relaxed text-muted-foreground ${className}`}
      role="note"
      aria-label="Service disclaimer"
    >
      <div className="flex items-center gap-2 text-foreground">
        <ShieldCheck className="h-4 w-4 text-primary" />
        <span className="text-sm font-semibold">Legal & Compliance Notice</span>
      </div>
      <p className="mt-2">
        TokDadddy is an <strong>independent digital marketing & promotion service</strong>.
        We help creators, artists and businesses promote their public content using
        legitimate advertising and audience-development methods.
      </p>
      <p className="mt-2">
        We are <strong>not affiliated with, endorsed by, sponsored by or operated by</strong>{" "}
        {platform}. All platform names, logos and trademarks are the property of their
        respective owners and are used here solely for descriptive identification.
      </p>
      <p className="mt-2">
        We never request your password, never access your private account data, and
        never engage in deceptive behaviour. Our services operate within each
        platform's public terms of service and comply with Google Ads policies on
        misrepresentation, transparency and user safety.
      </p>
    </div>
  );
};
